import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { LoggerService } from '../telemetry/logger.service';
import { Authentication } from './authentication';
import { TelemetryIdentifiers } from '../telemetry/telemetry-identifiers';
import { UserStateService } from '../user-state/user-state.service';
import { StoredAuthentication } from './stored-authentication';
import { MapStoredAuthenticationToAuthentication } from './mappers/map-stored-authentication-to-authentication';
import { MapAuthenticationToStoredAuthentication } from './mappers/map-authentication-to-stored-authentication';

/**
 * This service is used to authenticate to the web service.
 */
@Injectable({
	providedIn: 'root'
})
export class AuthenticationService {

	/** The path to the authentication controller. */
	private static readonly AUTHENTICATION_CONTROLLER_PATH: string = "/api/Authentication";

	/** The key for reading and writing the user's authentication details to and from local storage. */
	private static readonly STORED_AUTHENTICATION_LOCAL_STORAGE_KEY: string = "crownx-user-authentication";

	/** The default value for the stored authentication details. */
	private static readonly DEFAULT_STORED_AUTHENTICATION: StoredAuthentication = {
		IsAuthenticated: false,
		IsLockedOut: false,
		UserName: "",
		Email: ""
	};

	/** Captures the authentication details in local storage to reduce the overhead of reading from the device too frequently. */
	private storedAuthentication: StoredAuthentication = AuthenticationService.DEFAULT_STORED_AUTHENTICATION;

	/**
	 * @constructor
	 *
	 * @param httpClient The HTTP client to use for web requests.
	 * @param logger The logging service.
	 * @param userStateService The user state service.
	 */
	constructor(
		private readonly httpClient: HttpClient,
		private readonly logger: LoggerService,
		private readonly userStateService: UserStateService,
	) { }

	/**
	 * Gets the user's stored authentication details from local storage.
	 * @returns The stored authentication details from local storage, if available; otherwise, null.
	 * @readonly
	 */
	private get StoredAuthentication(): StoredAuthentication {
		if (this.storedAuthentication.IsAuthenticated)
			return this.storedAuthentication;

		const storedAuthenticationJson: string | null = localStorage.getItem(AuthenticationService.STORED_AUTHENTICATION_LOCAL_STORAGE_KEY);
		if (!storedAuthenticationJson)
			return AuthenticationService.DEFAULT_STORED_AUTHENTICATION;

		const storedAuthentication: StoredAuthentication | null = JSON.parse(storedAuthenticationJson);
		return storedAuthentication ?? AuthenticationService.DEFAULT_STORED_AUTHENTICATION;
	}

	/**
	 * Sets the user's stored authentication details in local storage.
	 * @param authentication The authentication to store in local storage.
	 */
	private set StoredAuthentication(authentication: StoredAuthentication) {
		this.storedAuthentication = authentication;
		const authenticationJson: string = JSON.stringify(authentication);
		localStorage.setItem(AuthenticationService.STORED_AUTHENTICATION_LOCAL_STORAGE_KEY, authenticationJson);
	}

	/**
	 * Indicates if the current session is authenticated.
	 *
	 * @readonly
	 */
	public get IsAuthenticated(): boolean {
		return Boolean(this.StoredAuthentication.IsAuthenticated);
	}

	/**
	 * The user-name of the current user or null if the user isn't authenticated.
	 *
	 * @readonly
	 */
	public get UserName(): string | null {
		return this.StoredAuthentication?.UserName ?? null;
	}

	/**
	 * The email of the current user or null if the user isn't authenticated.
	 *
	 * @readonly
	 */
	public get UserEmail(): string | null {
		return this.StoredAuthentication?.Email ?? null;
	}

	/**
	 * Passes credentials to the web service for validation.
	 *
	 * @param userName The user name supplied for authentication.
	 * @param password The password supplied for authentication.
	 * @returns An observable containing user's Authorization information.
	 */
	public Login$(userName: string, password: string): Observable<Authentication>
	{
		try {
			const credentials = {
				UserName: userName,
				Password: password
			};

			// Build the request.
			const url: string = AuthenticationService.AUTHENTICATION_CONTROLLER_PATH;
			const observable: Observable<Authentication> = this.httpClient.post<Authentication>(url, credentials, { withCredentials: true })
				.pipe(tap(
					(authInfo: Authentication) => {
						this.logger.TrackEvent(TelemetryIdentifiers.EVENT_LOGIN);
						const authentication: StoredAuthentication = MapAuthenticationToStoredAuthentication.Map(authInfo);
						this.StoredAuthentication = authentication;
					}
				));

			return observable;
		}
		catch (error: any) {
			this.logger.TrackError(`Error building Login request.`, error);
			return throwError(() => error);
		}

	}

	/**
	 * Gets the current user information based on the authentication cookie.
	 *
	 * @param force Indicates if the data should be reloaded if it's cached.
	 * @returns An observable containing user's Authorization information.
	 */
	public GetAuthentication$(force: boolean = false): Observable<Authentication> {
		if (force || !this.StoredAuthentication.IsAuthenticated) {
			try {
				// Build the request.
				const url: string = AuthenticationService.AUTHENTICATION_CONTROLLER_PATH;
				const observable: Observable<Authentication> = this.httpClient.get<Authentication>(url, { withCredentials: true })
					.pipe(tap(
						(authInfo: Authentication) => {
							const authentication: StoredAuthentication = MapAuthenticationToStoredAuthentication.Map(authInfo);
							this.StoredAuthentication = authentication;
						}
					));

				return observable;
			}
			catch (error: any) {
				this.logger.TrackError(`Error building GetUser request.`, error);
				return throwError(() => error);
			}
		}
		else
		{
			const authentication: Authentication = MapStoredAuthenticationToAuthentication.Map(this.StoredAuthentication);
			return of(authentication);
		}
	}

	/**
	 * Logs the current user out.
	 *
	 * @returns A void Observable.
	 */
	public Logout$(): Observable<void> {

		if (!this.userStateService.State.FilterTelemetry)
			this.logger.TrackEvent(TelemetryIdentifiers.EVENT_LOGOUT);

		this.ClearState();

		try {
			// Build the request.
			const url: string = AuthenticationService.AUTHENTICATION_CONTROLLER_PATH;
			return this.httpClient.delete<void>(url, { withCredentials: true });
		}
		catch (error: any) {
			this.logger.TrackError(`Error building Logout request.`, error);
			return throwError(() => error);
		}
	}

	/**
	 * Clears the current user's authentication state.
	 */
	public ClearState(): void {
		this.userStateService.ClearState();
		localStorage.removeItem(AuthenticationService.STORED_AUTHENTICATION_LOCAL_STORAGE_KEY);
	}

}